/**
 * La séance à deux : le parent tient le rôle du tuteur (lot P4).
 *
 * Ouverte par « M'asseoir à côté » depuis une feuille composée, une fiche du
 * carnet ou un exercice du soir (`ui/asseoir`). L'écran suit l'ordre du travail
 * et ne laisse pas le sauter :
 *
 * 1. **La consigne de posture en tête**, écrite par le serveur selon l'enfant —
 *    explicite et encourageante au collège, en partenaire au lycée. Elle vient
 *    avant l'énoncé parce que c'est au moment de s'asseoir qu'on la lit.
 * 2. **L'énoncé**, découpé de l'ouvrage ou en texte.
 * 3. **Les indices un échelon à la fois**. L'échelon est décidé par le serveur
 *    (`next_hint_level`, le même que la séance numérique) et l'état y est tenu :
 *    recharger la page ne rend pas un indice de plus, et ne remet rien à zéro.
 * 4. **Le corrigé et les points de vigilance seulement après une tentative** —
 *    une réponse donnée, ou « a cherché sans trouver » une fois tous les
 *    indices lus. Avant, ils ne sont **pas dans la réponse du serveur** : pas
 *    replié, absent (règle 2).
 * 5. **Le verdict**, qui seul écrit une observation de maîtrise (poids 0,8,
 *    modulé par le nombre d'indices — `core-api` §3.30).
 *
 * Rien de cet écran n'existe côté enfant : les routes sont sous
 * `/api/v2/enfants/{child_id}`, refusées à une session enfant.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { SeanceADeuxOut } from "@/api/schema";
import { EnonceImages, imagesDeLEnonce } from "@/ui/EnonceImage";
import { useEnfants } from "@/ui/enfants";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { Button, Card, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { TexteMathematique } from "@/ui/TexteMathematique";
import { entrainementsDe } from "@/v2/chemins";
import { type Choix, ChoixDuVerdict, confirmation, DIT } from "@/v2/ChoixDuVerdict";
import {
  boutonDIndice,
  etapeDe,
  pourquoiPasEncore,
  sansIndice,
  tentativeEnMots,
} from "@/v2/seanceADeux";

export function SeanceADeuxScreen() {
  const { childId = "", seanceId = "" } = useParams();
  const enfants = useEnfants();
  const prenom = enfants.find((enfant) => enfant.child_id === childId)?.display_name ?? "L'enfant";
  const [seance, setSeance] = useState<SeanceADeuxOut | null>(null);
  const [panne, setPanne] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [arme, setArme] = useState<Choix | null>(null);

  const params = { child_id: childId, seance_id: seanceId };

  useEffect(() => {
    let annule = false;
    void (async () => {
      try {
        const lue = await call("lireUneSeanceADeux", {
          params: { child_id: childId, seance_id: seanceId },
        });
        if (!annule) setSeance(lue);
      } catch (echec) {
        if (!annule) setPanne(echec instanceof ApiError ? echec.message : "Chargement impossible.");
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId, seanceId]);

  const agir = useCallback(async (geste: () => Promise<SeanceADeuxOut>) => {
    setEnvoi(true);
    setPanne(null);
    try {
      setSeance(await geste());
    } catch (echec) {
      setPanne(echec instanceof ApiError ? echec.message : "L'action a échoué.");
    } finally {
      setEnvoi(false);
    }
  }, []);

  if (!seance) {
    return (
      <Screen title="À côté de l'enfant">
        {panne ? <ErrorNote>{panne}</ErrorNote> : <Loading />}
      </Screen>
    );
  }

  const etape = etapeDe(seance);
  const enonces = imagesDeLEnonce(seance.enonce_urls);
  const corriges = imagesDeLEnonce(seance.corrige_urls);
  const indices = seance.indices ?? [];
  const vigilance = seance.vigilance ?? [];
  const bouton = boutonDIndice(seance);
  const retour = seance.ligne_id ? `${entrainementsDe(childId)}/composes` : `/parents/enfant/${childId}/prochain`;

  return (
    <Screen title={`À côté de ${prenom}`} lead={seance.competence}>
      <p className="notice">{seance.posture}</p>

      <Card>
        <p className="card__subtitle">L'énoncé</p>
        {enonces.length ? (
          <>
            <EtiquetteParascolaire nom={seance.parascolaire} />
            <EnonceImages images={enonces} statement={seance.enonce_texte ?? ""} />
          </>
        ) : seance.enonce_texte ? (
          <TexteMathematique className="exercice__enonce">{seance.enonce_texte}</TexteMathematique>
        ) : (
          <p className="muted small">L'énoncé est dans le cahier de {prenom}.</p>
        )}
      </Card>

      <Card>
        <p className="card__subtitle">Les indices</p>
        {sansIndice(seance) ? <p className="muted small">{sansIndice(seance)}</p> : null}
        {indices.length ? (
          <ol>
            {indices.map((indice, rang) => (
              <li key={rang}>
                <TexteMathematique>{indice}</TexteMathematique>
              </li>
            ))}
          </ol>
        ) : seance.indices_total > 0 ? (
          <p className="muted small">
            Aucun indice donné pour l'instant. Laissez d'abord {prenom} chercher.
          </p>
        ) : null}
        {bouton ? (
          <Button
            type="button"
            variant="quiet"
            disabled={envoi}
            onClick={() => void agir(() => call("devoilerUnIndice", { params }))}
          >
            {bouton}
          </Button>
        ) : null}
      </Card>

      {panne ? <ErrorNote>{panne}</ErrorNote> : null}

      <Card>
        <p className="card__subtitle">La réponse</p>
        {etape === "chercher" && !seance.tentative ? (
          <>
            <div className="screen__actions">
              <Button
                type="button"
                disabled={envoi}
                onClick={() =>
                  void agir(() =>
                    call("declarerUneTentative", { params, body: { tentative: "reponse" } }),
                  )
                }
              >
                {prenom} a répondu
              </Button>
              <Button
                type="button"
                variant="quiet"
                disabled={envoi || !seance.peut_declarer_cherche}
                onClick={() =>
                  void agir(() =>
                    call("declarerUneTentative", { params, body: { tentative: "cherche" } }),
                  )
                }
              >
                {prenom} a cherché sans trouver
              </Button>
            </div>
            {pourquoiPasEncore(seance, prenom) ? (
              <p className="muted small">{pourquoiPasEncore(seance, prenom)}</p>
            ) : null}
            <p className="muted small">Le corrigé s'ouvre quand {prenom} a donné une réponse.</p>
          </>
        ) : (
          <p className="muted small">{tentativeEnMots(seance, prenom)}</p>
        )}

        {seance.corrige_ouvert ? (
          <>
            {corriges.length ? (
              <>
                <p className="muted small">Le corrigé :</p>
                <EnonceImages images={corriges} statement="" />
              </>
            ) : seance.corrige_texte ? (
              <>
                <p className="muted small">Le corrigé :</p>
                <TexteMathematique>{seance.corrige_texte}</TexteMathematique>
              </>
            ) : (
              <p className="muted small">
                Pas de corrigé pour cet exercice : c'est vous qui relisez.
              </p>
            )}
            {vigilance.length ? (
              <>
                <p className="muted small">À regarder de près :</p>
                <ul className="small">
                  {vigilance.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="muted small">Aucun point de vigilance écrit pour cet exercice.</p>
            )}
          </>
        ) : null}
      </Card>

      {etape === "corriger" ? (
        <Card>
          <p className="card__subtitle">Votre verdict</p>
          <ChoixDuVerdict
            choisi={arme}
            choisir={setArme}
            desactive={envoi}
            pasFait={Boolean(seance.ligne_id)}
          />
          {arme ? (
            <div className="list__actions">
              <span className="muted small">{confirmation(arme)}</span>
              <Button
                disabled={envoi}
                onClick={() =>
                  void agir(() =>
                    call("jugerUneSeanceADeux", { params, body: { verdict: arme } }),
                  ).then(() => setArme(null))
                }
              >
                Confirmer
              </Button>
              <Button variant="quiet" disabled={envoi} onClick={() => setArme(null)}>
                Annuler
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {etape === "jugee" && seance.verdict ? (
        <p className="notice">
          {seance.verdict === "pas_fait"
            ? "Pas fait — l'exercice revient dans les exercices à tirer."
            : `Jugé ${DIT[seance.verdict] ?? seance.verdict}.`}
        </p>
      ) : null}

      <p className="muted small">
        <Link to={retour}>Revenir</Link>
      </p>
    </Screen>
  );
}
